import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Collapse,
  Stack,
  Divider,
  Tooltip,
  Badge,
  Menu,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
  Layers as SectionIcon,
  Settings as MachineIcon,
  Bolt as PowerIcon,
  WaterDrop as WaterIcon,
  CloudQueue as SteamIcon,
  LocalGasStation as GasIcon,
  Business as DepartmentIcon,
  Speed as MeterIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface Meter {
  id: number;
  meter_name: string;
  meter_code: string;
  meter_type: string;
  unit: string;
  description?: string;
}

interface Machine {
  id: number;
  machine_name: string;
  machine_code: string;
  description?: string;
  meters: Meter[];
}

interface Section {
  id: number;
  section_name: string;
  description?: string;
  meters: Meter[];
  machines: Machine[];
}

interface Architecture {
  department: {
    name: string;
    meters: Meter[];
  };
  sections: Section[];
}

interface HoverTrendPoint {
  period: string;
  steam: number;
  power: number;
  water: number;
}

interface HoverLatestReading {
  meter_type: string;
  meter_code: string;
  unit: string;
  reading_value: number;
  reading_date: string;
  reading_time: string;
}

interface HoverInsight {
  trend: HoverTrendPoint[];
  latest_by_type: Record<string, HoverLatestReading>;
}

const MeterArchitecture: React.FC = () => {
  const [architecture, setArchitecture] = useState<Architecture | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [sectionDialog, setSectionDialog] = useState(false);
  const [machineDialog, setMachineDialog] = useState(false);
  const [meterDialog, setMeterDialog] = useState(false);
  const [editSectionDialog, setEditSectionDialog] = useState(false);
  const [editMachineDialog, setEditMachineDialog] = useState(false);
  const [editMeterDialog, setEditMeterDialog] = useState(false);
  const [moveMeterDialog, setMoveMeterDialog] = useState(false);
  const [moveMachineDialog, setMoveMachineDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Menu states
  const [sectionMenu, setSectionMenu] = useState<{ anchorEl: HTMLElement; section: Section } | null>(null);
  const [machineMenu, setMachineMenu] = useState<{ anchorEl: HTMLElement; machine: Machine; sectionId: number } | null>(null);
  const [meterMenu, setMeterMenu] = useState<{ anchorEl: HTMLElement; meter: Meter } | null>(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; id: number; name: string } | null>(null);

  // Form states
  const [sectionForm, setSectionForm] = useState({ id: 0, section_name: '', description: '' });
  const [machineForm, setMachineForm] = useState({
    id: 0,
    section_id: '',
    machine_name: '',
    machine_code: '',
    description: '',
  });
  const [meterForm, setMeterForm] = useState({
    id: 0,
    meter_name: '',
    meter_code: '',
    meter_type: 'Power',
    unit: '',
    assignment_level: 'Department',
    assignment_id: '',
    description: '',
  });

  useEffect(() => {
    fetchArchitecture();
  }, []);

  useEffect(() => {
    // Auto-expand all sections when architecture loads
    if (architecture && architecture.sections.length > 0) {
      setExpandedSections(architecture.sections.map(s => s.id));
    }
  }, [architecture]);

  const fetchArchitecture = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/energy-management/meters/architecture', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArchitecture(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load architecture');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/energy-management/sections', sectionForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Section added successfully');
      setSectionDialog(false);
      setSectionForm({ id: 0, section_name: '', description: '' });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add section');
    }
  };

  const handleEditSection = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/energy-management/sections/${sectionForm.id}`, sectionForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Section updated successfully');
      setEditSectionDialog(false);
      setSectionForm({ id: 0, section_name: '', description: '' });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update section');
    }
  };

  const handleAddMachine = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/energy-management/machines', machineForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Machine added successfully');
      setMachineDialog(false);
      setMachineForm({ id: 0, section_id: '', machine_name: '', machine_code: '', description: '' });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add machine');
    }
  };

  const handleEditMachine = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/energy-management/machines/${machineForm.id}`, machineForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Machine updated successfully');
      setEditMachineDialog(false);
      setMachineForm({ id: 0, section_id: '', machine_name: '', machine_code: '', description: '' });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update machine');
    }
  };

  const handleMoveMachine = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/energy-management/machines/${machineForm.id}`, 
        { section_id: machineForm.section_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Machine moved successfully');
      setMoveMachineDialog(false);
      setMachineForm({ id: 0, section_id: '', machine_name: '', machine_code: '', description: '' });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to move machine');
    }
  };

  const handleAddMeter = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...meterForm,
        assignment_id: meterForm.assignment_level === 'Department' ? null : meterForm.assignment_id,
      };
      await axios.post('/api/energy-management/meters', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Meter added successfully');
      setMeterDialog(false);
      setMeterForm({
        id: 0,
        meter_name: '',
        meter_code: '',
        meter_type: 'Power',
        unit: '',
        assignment_level: 'Department',
        assignment_id: '',
        description: '',
      });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add meter');
    }
  };

  const handleEditMeter = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/energy-management/meters/${meterForm.id}`, meterForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Meter updated successfully');
      setEditMeterDialog(false);
      setMeterForm({
        id: 0,
        meter_name: '',
        meter_code: '',
        meter_type: 'Power',
        unit: '',
        assignment_level: 'Department',
        assignment_id: '',
        description: '',
      });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update meter');
    }
  };

  const handleMoveMeter = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        assignment_level: meterForm.assignment_level,
        assignment_id: meterForm.assignment_level === 'Department' ? null : meterForm.assignment_id,
      };
      await axios.put(`/api/energy-management/meters/${meterForm.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Meter moved successfully');
      setMoveMeterDialog(false);
      setMeterForm({
        id: 0,
        meter_name: '',
        meter_code: '',
        meter_type: 'Power',
        unit: '',
        assignment_level: 'Department',
        assignment_id: '',
        description: '',
      });
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to move meter');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (deleteTarget.type === 'section') {
        endpoint = `/api/energy-management/sections/${deleteTarget.id}`;
      } else if (deleteTarget.type === 'machine') {
        endpoint = `/api/energy-management/machines/${deleteTarget.id}`;
      } else if (deleteTarget.type === 'meter') {
        endpoint = `/api/energy-management/meters/${deleteTarget.id}`;
      }

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(`${deleteTarget.type.charAt(0).toUpperCase() + deleteTarget.type.slice(1)} deleted successfully`);
      setDeleteDialog(false);
      setDeleteTarget(null);
      fetchArchitecture();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const openEditSection = (section: Section) => {
    setSectionForm({
      id: section.id,
      section_name: section.section_name,
      description: section.description || '',
    });
    setEditSectionDialog(true);
  };

  const openEditMachine = (machine: Machine, sectionId: number) => {
    setMachineForm({
      id: machine.id,
      section_id: sectionId.toString(),
      machine_name: machine.machine_name,
      machine_code: machine.machine_code,
      description: machine.description || '',
    });
    setEditMachineDialog(true);
  };

  const openMoveMachine = (machine: Machine, currentSectionId: number) => {
    setMachineForm({
      id: machine.id,
      section_id: currentSectionId.toString(),
      machine_name: machine.machine_name,
      machine_code: machine.machine_code,
      description: machine.description || '',
    });
    setMoveMachineDialog(true);
  };

  const openEditMeter = (meter: Meter) => {
    setMeterForm({
      id: meter.id,
      meter_name: meter.meter_name,
      meter_code: meter.meter_code,
      meter_type: meter.meter_type,
      unit: meter.unit,
      assignment_level: 'Department',
      assignment_id: '',
      description: meter.description || '',
    });
    setEditMeterDialog(true);
  };

  const openMoveMeter = (meter: Meter) => {
    setMeterForm({
      id: meter.id,
      meter_name: meter.meter_name,
      meter_code: meter.meter_code,
      meter_type: meter.meter_type,
      unit: meter.unit,
      assignment_level: 'Department',
      assignment_id: '',
      description: meter.description || '',
    });
    setMoveMeterDialog(true);
  };

  const openDeleteDialog = (type: string, id: number, name: string) => {
    setDeleteTarget({ type, id, name });
    setDeleteDialog(true);
  };

  const getMeterIcon = (meterType: string) => {
    switch (meterType) {
      case 'Power':
        return <PowerIcon sx={{ color: '#FFC107', fontSize: 14 }} />;
      case 'Water':
        return <WaterIcon sx={{ color: '#2196F3', fontSize: 14 }} />;
      case 'Steam':
        return <SteamIcon sx={{ color: '#FF5722', fontSize: 14 }} />;
      case 'Gas':
        return <GasIcon sx={{ color: '#9C27B0', fontSize: 14 }} />;
      default:
        return <MeterIcon sx={{ color: '#757575', fontSize: 14 }} />;
    }
  };

  const getMeterColor = (meterType: string) => {
    switch (meterType) {
      case 'Power':
        return '#FFC107';
      case 'Water':
        return '#2196F3';
      case 'Steam':
        return '#FF5722';
      case 'Gas':
        return '#9C27B0';
      default:
        return '#757575';
    }
  };

  // State for expandable cards and detail view
  const [expandedSections, setExpandedSections] = useState<number[]>([]);
  const [expandedMachines, setExpandedMachines] = useState<number[]>([]);
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    type: 'section' | 'machine' | 'meter' | 'department' | null;
    data: any;
  }>({ open: false, type: null, data: null });
  const [hoverInsights, setHoverInsights] = useState<Record<string, HoverInsight>>({});
  const [hoverLoading, setHoverLoading] = useState<Record<string, boolean>>({});

  const toggleSection = (sectionId: number) => {
    setExpandedSections(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
    );
  };

  const toggleMachine = (machineId: number) => {
    setExpandedMachines(prev =>
      prev.includes(machineId) ? prev.filter(id => id !== machineId) : [...prev, machineId]
    );
  };

  const openDetail = (type: 'section' | 'machine' | 'meter' | 'department', data: any) => {
    setDetailDialog({ open: true, type, data });
  };

  const loadHoverInsight = async (cacheKey: string, level: 'department' | 'section' | 'machine' | 'meter', id?: number) => {
    if (hoverInsights[cacheKey] || hoverLoading[cacheKey]) return;

    try {
      setHoverLoading((prev) => ({ ...prev, [cacheKey]: true }));
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/energy-management/analytics/hover-insights', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          level,
          ...(id ? { id } : {}),
        },
      });
      setHoverInsights((prev) => ({ ...prev, [cacheKey]: response.data }));
    } catch (err) {
      setHoverInsights((prev) => ({
        ...prev,
        [cacheKey]: { trend: [], latest_by_type: {} },
      }));
    } finally {
      setHoverLoading((prev) => ({ ...prev, [cacheKey]: false }));
    }
  };

  const buildLinePath = (values: number[], width: number, height: number) => {
    if (values.length === 0) return '';
    const maxValue = Math.max(...values, 1);
    return values
      .map((value, index) => {
        const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
        const y = height - (value / maxValue) * height;
        return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');
  };

  const renderMiniTrendChart = (trend: HoverTrendPoint[]) => {
    if (!trend || trend.length === 0) {
      return (
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
          No recent monthly trend data
        </Typography>
      );
    }

    const width = 180;
    const height = 46;
    const steamValues = trend.map((item) => item.steam || 0);
    const powerValues = trend.map((item) => item.power || 0);
    const waterValues = trend.map((item) => item.water || 0);

    return (
      <Box>
        <Box sx={{ mb: 0.5 }}>
          <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
            <path d={buildLinePath(steamValues, width, height)} stroke="#FF5722" strokeWidth="1.6" fill="none" />
            <path d={buildLinePath(powerValues, width, height)} stroke="#FFC107" strokeWidth="1.6" fill="none" />
            <path d={buildLinePath(waterValues, width, height)} stroke="#2196F3" strokeWidth="1.6" fill="none" />
          </svg>
        </Box>
        <Stack direction="row" spacing={1.2}>
          <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#FF5722' }}>Steam</Typography>
          <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#FFC107' }}>Power</Typography>
          <Typography variant="caption" sx={{ fontSize: '0.62rem', color: '#2196F3' }}>Water</Typography>
        </Stack>
      </Box>
    );
  };

  const renderHoverContent = (cacheKey: string) => {
    if (hoverLoading[cacheKey]) {
      return (
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption">Loading monthly trend...</Typography>
        </Box>
      );
    }

    const data = hoverInsights[cacheKey];
    const latestSteam = data?.latest_by_type?.steam;
    const latestPower = data?.latest_by_type?.power;
    const latestWater = data?.latest_by_type?.water;

    return (
      <Box sx={{ p: 1, minWidth: 210 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.8 }}>
          Monthly Consumption Trend
        </Typography>
        {renderMiniTrendChart(data?.trend || [])}
        <Divider sx={{ my: 0.8 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
          Last Recorded Reading
        </Typography>
        <Stack spacing={0.25}>
          <Typography variant="caption" sx={{ fontSize: '0.66rem' }}>
            Steam: {latestSteam ? `${latestSteam.reading_value.toLocaleString()} ${latestSteam.unit}` : 'N/A'}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.66rem' }}>
            Power: {latestPower ? `${latestPower.reading_value.toLocaleString()} ${latestPower.unit}` : 'N/A'}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.66rem' }}>
            Water: {latestWater ? `${latestWater.reading_value.toLocaleString()} ${latestWater.unit}` : 'N/A'}
          </Typography>
        </Stack>
      </Box>
    );
  };

  // Compact Meter Card - Small Rectangle
  const MeterCard = ({ meter, onClick }: { meter: Meter; onClick?: () => void }) => (
    <Tooltip
      arrow
      placement="top"
      title={renderHoverContent(`meter-${meter.id}`)}
      onOpen={() => loadHoverInsight(`meter-${meter.id}`, 'meter', meter.id)}
    >
      <Paper
        elevation={1}
        onClick={onClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          cursor: 'pointer',
          borderLeft: `3px solid ${getMeterColor(meter.meter_type)}`,
          backgroundColor: `${getMeterColor(meter.meter_type)}10`,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: `${getMeterColor(meter.meter_type)}20`,
            transform: 'translateY(-2px)',
            boxShadow: 2,
          },
        }}
      >
        {getMeterIcon(meter.meter_type)}
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
          {meter.meter_code}
        </Typography>
      </Paper>
    </Tooltip>
  );

  // Compact Machine Card - Org Chart Style
  const MachineCard = ({ machine, sectionId, isExpanded, onToggle, onClick, isFirst, isLast, totalMachines }: {
    machine: Machine;
    sectionId: number;
    isExpanded: boolean;
    onToggle: () => void;
    onClick: () => void;
    isFirst: boolean;
    isLast: boolean;
    totalMachines: number;
  }) => (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
      {/* Vertical connector line from horizontal bar above (only if multiple machines) */}
      {totalMachines > 1 && (
        <Box sx={{
          width: 3,
          height: 35,
          backgroundColor: '#00695C',
          mb: 0,
        }} />
      )}
      
      <Tooltip
        arrow
        placement="top"
        title={renderHoverContent(`machine-${machine.id}`)}
        onOpen={() => loadHoverInsight(`machine-${machine.id}`, 'machine', machine.id)}
      >
      <Paper
        elevation={2}
        sx={{
          width: 140,
          p: 1.5,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px solid #00695C',
          backgroundColor: '#fff',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
            borderColor: '#004d40',
          },
        }}
      >
        <Box onClick={onClick}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
            <MachineIcon sx={{ color: '#00695C', fontSize: 24 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#00695C', mb: 0.3 }}>
            {machine.machine_name}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
            {machine.machine_code}
          </Typography>
          <Badge
            badgeContent={machine.meters.length}
            sx={{ '& .MuiBadge-badge': { backgroundColor: '#FFC107', color: '#000', fontSize: '0.65rem', fontWeight: 700 } }}
          >
            <Chip
              label="Meters"
              size="small"
              sx={{ fontSize: '0.6rem', height: 18, cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
            />
          </Badge>
        </Box>
        
        {/* Meters - Expandable */}
        <Collapse in={isExpanded}>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={0.5} alignItems="center">
            {machine.meters.map((meter) => (
              <MeterCard
                key={meter.id}
                meter={meter}
                onClick={() => openDetail('meter', meter)}
              />
            ))}
            {machine.meters.length === 0 && (
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', fontStyle: 'italic' }}>
                No meters
              </Typography>
            )}
          </Stack>
        </Collapse>
        
        {/* Action Button */}
        <Divider sx={{ my: 1 }} />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMachineMenu({ anchorEl: e.currentTarget, machine, sectionId });
          }}
          sx={{ p: 0.3 }}
        >
          <MoreVertIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Paper>
      </Tooltip>
    </Box>
  );

  // Compact Section Card - Org Chart Style
  const SectionCard = ({ section, isExpanded, onToggle, onClick, isFirst, isLast, totalSections }: {
    section: Section;
    isExpanded: boolean;
    onToggle: () => void;
    onClick: () => void;
    isFirst: boolean;
    isLast: boolean;
    totalSections: number;
  }) => (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 200, flexShrink: 0 }}>
      {/* Vertical connector from horizontal line to section card */}
      {totalSections > 1 && (
        <Box sx={{
          width: 3,
          height: 35,
          backgroundColor: '#1976d2',
          mb: 0,
        }} />
      )}
      
      <Tooltip
        arrow
        placement="top"
        title={renderHoverContent(`section-${section.id}`)}
        onOpen={() => loadHoverInsight(`section-${section.id}`, 'section', section.id)}
      >
      <Paper
        elevation={3}
        sx={{
          width: 180,
          p: 1.5,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px solid #1976d2',
          backgroundColor: '#fff',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
            borderColor: '#0d47a1',
          },
        }}
      >
        <Box onClick={onClick}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
            <SectionIcon sx={{ color: '#1976d2', fontSize: 28 }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1976d2', mb: 0.5 }}>
            {section.section_name}
          </Typography>
          <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mb: 0.5 }}>
            <Badge
              badgeContent={section.machines.length}
              sx={{ '& .MuiBadge-badge': { backgroundColor: '#00695C', color: '#fff', fontSize: '0.6rem' } }}
            >
              <Chip label="Machines" size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
            </Badge>
            {section.meters.length > 0 && (
              <Badge
                badgeContent={section.meters.length}
                sx={{ '& .MuiBadge-badge': { backgroundColor: '#FFC107', color: '#000', fontSize: '0.6rem' } }}
              >
                <Chip label="Meters" size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
              </Badge>
            )}
          </Stack>
          <Button
            size="small"
            variant="text"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            sx={{ fontSize: '0.65rem', textTransform: 'none', p: 0.3 }}
          >
            {isExpanded ? 'Hide' : 'Show'} Machines
          </Button>
        </Box>
        
        {/* Section Direct Meters */}
        {section.meters.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 0.5 }} />
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Section Meters:
            </Typography>
            <Stack spacing={0.3} alignItems="center">
              {section.meters.map((meter) => (
                <MeterCard
                  key={meter.id}
                  meter={meter}
                  onClick={() => openDetail('meter', meter)}
                />
              ))}
            </Stack>
          </Box>
        )}
        
        {/* Action Button */}
        <Divider sx={{ my: 1 }} />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSectionMenu({ anchorEl: e.currentTarget, section });
          }}
          sx={{ p: 0.3 }}
        >
          <MoreVertIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Paper>
      </Tooltip>
      
      {/* Machines below section - All on same level, horizontally scrollable */}
      <Collapse in={isExpanded}>
        {section.machines.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '100%', mt: 1 }}>
            {/* Vertical line down from section */}
            <Box sx={{
              width: 3,
              height: 35,
              backgroundColor: '#00695C',
            }} />
            
            {/* Horizontal connector bar for machines */}
            {section.machines.length > 1 && (
              <Box sx={{ 
                position: 'relative', 
                width: `${section.machines.length * 160}px`,
                height: 3, 
                backgroundColor: '#00695C', 
                mb: 0 
              }} />
            )}
            
            {/* Machines row - All on same level, no wrapping */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'nowrap',
              mt: 0,
              overflowX: 'visible',
              minWidth: 'min-content',
            }}>
              {section.machines.map((machine, idx) => (
                <MachineCard
                  key={machine.id}
                  machine={machine}
                  sectionId={section.id}
                  isExpanded={expandedMachines.includes(machine.id)}
                  onToggle={() => toggleMachine(machine.id)}
                  onClick={() => openDetail('machine', { machine, sectionName: section.section_name })}
                  isFirst={idx === 0}
                  isLast={idx === section.machines.length - 1}
                  totalMachines={section.machines.length}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ py: 2, px: 3, backgroundColor: '#f5f5f5', borderRadius: 1, mt: 2 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
              No machines in this section
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography>Loading architecture...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Success/Error Messages */}
      <Collapse in={!!success}>
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Collapse>
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>

      {/* Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#00695C', mb: 0.5 }}>
              Energy Meter Architecture
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hierarchical flow diagram - Department → Sections → Machines → Meters
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchArchitecture}
                size="small"
                sx={{ borderColor: '#00695C', color: '#00695C' }}
              >
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Professional Org Chart Architecture - Full Width */}
      {architecture && (
        <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, minHeight: '70vh', width: '100%', pb: 3, overflowX: 'auto' }}>
          {/* Level 0: Department - Top of Org Chart */}
          <Box sx={{ textAlign: 'center', mb: 0 }}>
            <Tooltip
              arrow
              placement="top"
              title={renderHoverContent('department-all')}
              onOpen={() => loadHoverInsight('department-all', 'department')}
            >
            <Paper
              elevation={4}
              onClick={() => openDetail('department', architecture.department)}
              sx={{
                display: 'inline-block',
                p: 2,
                minWidth: 220,
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #00695C 0%, #004d40 100%)',
                color: 'white',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 8,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
                <DepartmentIcon sx={{ fontSize: 36 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {architecture.department.name}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 1 }}>
                Department Level
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Badge
                  badgeContent={architecture.sections.length}
                  sx={{ '& .MuiBadge-badge': { backgroundColor: '#1976d2', color: '#fff', fontSize: '0.65rem' } }}
                >
                  <Chip label="Sections" size="small" sx={{ color: 'white', borderColor: 'white', fontSize: '0.65rem' }} variant="outlined" />
                </Badge>
                {architecture.department.meters.length > 0 && (
                  <Badge
                    badgeContent={architecture.department.meters.length}
                    sx={{ '& .MuiBadge-badge': { backgroundColor: '#FFC107', color: '#000', fontSize: '0.65rem', fontWeight: 700 } }}
                  >
                    <Chip label="Meters" size="small" sx={{ color: 'white', borderColor: 'white', fontSize: '0.65rem' }} variant="outlined" />
                  </Badge>
                )}
              </Stack>
              <Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Tooltip title="Add Section">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setSectionDialog(true); }}
                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add Department Meter">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMeterForm({
                        id: 0,
                        meter_name: '',
                        meter_code: '',
                        meter_type: 'Power',
                        unit: '',
                        assignment_level: 'Department',
                        assignment_id: '',
                        description: '',
                      });
                      setMeterDialog(true);
                    }}
                    sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } }}
                  >
                    <MeterIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
            </Tooltip>
            {architecture.sections.length > 0 && (
              <Box sx={{ width: 3, height: 40, backgroundColor: '#1976d2', mx: 'auto' }} />
            )}
          </Box>

          {/* Level 1: Sections - Second Level - Horizontal Scrollable */}
          {architecture.sections.length > 0 && (
            <Box sx={{ mb: 3, position: 'relative', width: '100%' }}>
              {/* Connector system wrapper - ensures proper alignment */}
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: '100%' }}>
                {/* Horizontal spanning line across all sections */}
                {architecture.sections.length > 1 && (
                  <Box sx={{
                    width: `${architecture.sections.length * 220}px`,
                    minWidth: '100%',
                    height: 3,
                    backgroundColor: '#1976d2',
                    mb: 0,
                  }} />
                )}
                
                {/* Sections Row - All on same level, horizontal scroll */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start',
                  gap: 4,
                  flexWrap: 'nowrap',
                  mt: 0,
                  pb: 2,
                  width: 'max-content',
                  minWidth: '100%',
                }}>
                {architecture.sections.map((section, index) => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    isExpanded={expandedSections.includes(section.id)}
                    onToggle={() => toggleSection(section.id)}
                    onClick={() => openDetail('section', section)}
                    isFirst={index === 0}
                    isLast={index === architecture.sections.length - 1}
                    totalSections={architecture.sections.length}
                  />
                ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Empty State */}
          {architecture.sections.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Sections Created Yet
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setSectionDialog(true)}
                sx={{ mt: 2, backgroundColor: '#00695C' }}
              >
                Add First Section
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Detail View Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, type: null, data: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, backgroundColor: '#00695C', color: 'white' }}>
          {detailDialog.type === 'department' && 'Department Details'}
          {detailDialog.type === 'section' && 'Section Details'}
          {detailDialog.type === 'machine' && 'Machine Details'}
          {detailDialog.type === 'meter' && 'Meter Details'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {detailDialog.type === 'department' && detailDialog.data && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {detailDialog.data.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#1976d2', color: 'white' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {architecture?.sections.length || 0}
                    </Typography>
                    <Typography variant="body2">Total Sections</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#FFC107' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {detailDialog.data.meters.length}
                    </Typography>
                    <Typography variant="body2">Department Meters</Typography>
                  </Paper>
                </Grid>
                {detailDialog.data.meters.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                      Department-Level Meters:
                    </Typography>
                    <Stack spacing={1}>
                      {detailDialog.data.meters.map((meter: Meter) => (
                        <Paper key={meter.id} sx={{ p: 1.5, borderLeft: `4px solid ${getMeterColor(meter.meter_type)}` }}>
                          <Grid container alignItems="center">
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getMeterIcon(meter.meter_type)}
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {meter.meter_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {meter.meter_code}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Chip label={meter.meter_type} size="small" sx={{ backgroundColor: getMeterColor(meter.meter_type), color: 'white' }} />
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2">{meter.unit}</Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          
          {detailDialog.type === 'section' && detailDialog.data && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {detailDialog.data.section_name}
                  </Typography>
                  {detailDialog.data.description && (
                    <Typography variant="body2" color="text.secondary">
                      {detailDialog.data.description}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#00695C', color: 'white' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {detailDialog.data.machines.length}
                    </Typography>
                    <Typography variant="body2">Machines</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#FFC107' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {detailDialog.data.meters.length}
                    </Typography>
                    <Typography variant="body2">Section Meters</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, backgroundColor: '#1976d2', color: 'white' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {detailDialog.data.machines.reduce((sum: number, m: Machine) => sum + m.meters.length, 0)}
                    </Typography>
                    <Typography variant="body2">Machine Meters</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setMachineForm({
                        id: 0,
                        section_id: detailDialog.data.id.toString(),
                        machine_name: '',
                        machine_code: '',
                        description: '',
                      });
                      setMachineDialog(true);
                      setDetailDialog({ open: false, type: null, data: null });
                    }}
                    sx={{ mr: 1 }}
                  >
                    Add Machine
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<MeterIcon />}
                    onClick={() => {
                      setMeterForm({
                        id: 0,
                        meter_name: '',
                        meter_code: '',
                        meter_type: 'Power',
                        unit: '',
                        assignment_level: 'Section',
                        assignment_id: detailDialog.data.id.toString(),
                        description: '',
                      });
                      setMeterDialog(true);
                      setDetailDialog({ open: false, type: null, data: null });
                    }}
                  >
                    Add Meter
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {detailDialog.type === 'machine' && detailDialog.data && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {detailDialog.data.machine.machine_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Code: {detailDialog.data.machine.machine_code}
                  </Typography>
                  <Chip label={detailDialog.data.sectionName} size="small" sx={{ backgroundColor: '#1976d2', color: 'white' }} />
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#FFC107' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {detailDialog.data.machine.meters.length}
                    </Typography>
                    <Typography variant="body2">Assigned Meters</Typography>
                  </Paper>
                </Grid>
                {detailDialog.data.machine.meters.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                      Meters:
                    </Typography>
                    <Stack spacing={1}>
                      {detailDialog.data.machine.meters.map((meter: Meter) => (
                        <Paper key={meter.id} sx={{ p: 1.5, borderLeft: `4px solid ${getMeterColor(meter.meter_type)}` }}>
                          <Grid container alignItems="center">
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getMeterIcon(meter.meter_type)}
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {meter.meter_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {meter.meter_code}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={3}>
                              <Chip label={meter.meter_type} size="small" sx={{ backgroundColor: getMeterColor(meter.meter_type), color: 'white' }} />
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="body2">{meter.unit}</Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<MeterIcon />}
                    onClick={() => {
                      setMeterForm({
                        id: 0,
                        meter_name: '',
                        meter_code: '',
                        meter_type: 'Power',
                        unit: '',
                        assignment_level: 'Machine',
                        assignment_id: detailDialog.data.machine.id.toString(),
                        description: '',
                      });
                      setMeterDialog(true);
                      setDetailDialog({ open: false, type: null, data: null });
                    }}
                  >
                    Add Meter to This Machine
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {detailDialog.type === 'meter' && detailDialog.data && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {getMeterIcon(detailDialog.data.meter_type)}
                    <Typography variant="h6">
                      {detailDialog.data.meter_name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Meter Code
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {detailDialog.data.meter_code}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Meter Type
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={detailDialog.data.meter_type}
                      sx={{ backgroundColor: getMeterColor(detailDialog.data.meter_type), color: 'white' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Unit
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {detailDialog.data.unit}
                  </Typography>
                </Grid>
                {detailDialog.data.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2">
                      {detailDialog.data.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, type: null, data: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Section Actions Menu */}
      <Menu
        anchorEl={sectionMenu?.anchorEl}
        open={Boolean(sectionMenu)}
        onClose={() => setSectionMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (sectionMenu) {
              openEditSection(sectionMenu.section);
              setSectionMenu(null);
            }
          }}
        >
          <EditIcon sx={{ fontSize: 18, mr: 1 }} /> Edit Section
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (sectionMenu) {
              openDeleteDialog('section', sectionMenu.section.id, sectionMenu.section.section_name);
              setSectionMenu(null);
            }
          }}
          sx={{ color: '#f44336' }}
        >
          <DeleteIcon sx={{ fontSize: 18, mr: 1 }} /> Delete Section
        </MenuItem>
      </Menu>

      {/* Machine Actions Menu */}
      <Menu
        anchorEl={machineMenu?.anchorEl}
        open={Boolean(machineMenu)}
        onClose={() => setMachineMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (machineMenu) {
              openEditMachine(machineMenu.machine, machineMenu.sectionId);
              setMachineMenu(null);
            }
          }}
        >
          <EditIcon sx={{ fontSize: 18, mr: 1 }} /> Edit Machine
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (machineMenu) {
              openMoveMachine(machineMenu.machine, machineMenu.sectionId);
              setMachineMenu(null);
            }
          }}
        >
          <MoveIcon sx={{ fontSize: 18, mr: 1 }} /> Move to Section
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (machineMenu) {
              openDeleteDialog('machine', machineMenu.machine.id, machineMenu.machine.machine_name);
              setMachineMenu(null);
            }
          }}
          sx={{ color: '#f44336' }}
        >
          <DeleteIcon sx={{ fontSize: 18, mr: 1 }} /> Delete Machine
        </MenuItem>
      </Menu>

      {/* Meter Actions Menu */}
      <Menu
        anchorEl={meterMenu?.anchorEl}
        open={Boolean(meterMenu)}
        onClose={() => setMeterMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (meterMenu) {
              openEditMeter(meterMenu.meter);
              setMeterMenu(null);
            }
          }}
        >
          <EditIcon sx={{ fontSize: 18, mr: 1 }} /> Edit Meter
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (meterMenu) {
              openMoveMeter(meterMenu.meter);
              setMeterMenu(null);
            }
          }}
        >
          <MoveIcon sx={{ fontSize: 18, mr: 1 }} /> Move Meter
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (meterMenu) {
              openDeleteDialog('meter', meterMenu.meter.id, meterMenu.meter.meter_name);
              setMeterMenu(null);
            }
          }}
          sx={{ color: '#f44336' }}
        >
          <DeleteIcon sx={{ fontSize: 18, mr: 1 }} /> Delete Meter
        </MenuItem>
      </Menu>

      {/* Add Section Dialog */}
      <Dialog open={sectionDialog} onClose={() => setSectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#00695C' }}>Add New Section</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Section Name"
              value={sectionForm.section_name}
              onChange={(e) => setSectionForm({ ...sectionForm, section_name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={sectionForm.description}
              onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDialog(false)}>Cancel</Button>
          <Button onClick={handleAddSection} variant="contained" sx={{ backgroundColor: '#00695C' }}>
            Add Section
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Section Dialog */}
      <Dialog open={editSectionDialog} onClose={() => setEditSectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#00695C' }}>Edit Section</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Section Name"
              value={sectionForm.section_name}
              onChange={(e) => setSectionForm({ ...sectionForm, section_name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={sectionForm.description}
              onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSectionDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSection} variant="contained" sx={{ backgroundColor: '#00695C' }}>
            Update Section
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Machine Dialog */}
      <Dialog open={machineDialog} onClose={() => setMachineDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1976d2' }}>Add New Machine</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              select
              label="Section"
              value={machineForm.section_id}
              onChange={(e) => setMachineForm({ ...machineForm, section_id: e.target.value })}
              required
            >
              {architecture?.sections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.section_name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Machine Name"
              value={machineForm.machine_name}
              onChange={(e) => setMachineForm({ ...machineForm, machine_name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Machine Code"
              value={machineForm.machine_code}
              onChange={(e) => setMachineForm({ ...machineForm, machine_code: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={machineForm.description}
              onChange={(e) => setMachineForm({ ...machineForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMachineDialog(false)}>Cancel</Button>
          <Button onClick={handleAddMachine} variant="contained" sx={{ backgroundColor: '#1976d2' }}>
            Add Machine
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Machine Dialog */}
      <Dialog open={editMachineDialog} onClose={() => setEditMachineDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1976d2' }}>Edit Machine</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Machine Name"
              value={machineForm.machine_name}
              onChange={(e) => setMachineForm({ ...machineForm, machine_name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Machine Code"
              value={machineForm.machine_code}
              onChange={(e) => setMachineForm({ ...machineForm, machine_code: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={machineForm.description}
              onChange={(e) => setMachineForm({ ...machineForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMachineDialog(false)}>Cancel</Button>
          <Button onClick={handleEditMachine} variant="contained" sx={{ backgroundColor: '#1976d2' }}>
            Update Machine
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Machine Dialog */}
      <Dialog open={moveMachineDialog} onClose={() => setMoveMachineDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1976d2' }}>Move Machine to Different Section</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Moving: <strong>{machineForm.machine_name}</strong>
            </Alert>
            <TextField
              fullWidth
              select
              label="New Section"
              value={machineForm.section_id}
              onChange={(e) => setMachineForm({ ...machineForm, section_id: e.target.value })}
              required
            >
              {architecture?.sections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.section_name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveMachineDialog(false)}>Cancel</Button>
          <Button onClick={handleMoveMachine} variant="contained" sx={{ backgroundColor: '#1976d2' }}>
            Move Machine
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Meter Dialog */}
      <Dialog open={meterDialog} onClose={() => setMeterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#FFC107' }}>Add New Meter</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Meter Name"
              value={meterForm.meter_name}
              onChange={(e) => setMeterForm({ ...meterForm, meter_name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Meter Code"
              value={meterForm.meter_code}
              onChange={(e) => setMeterForm({ ...meterForm, meter_code: e.target.value })}
              required
            />
            <TextField
              fullWidth
              select
              label="Meter Type"
              value={meterForm.meter_type}
              onChange={(e) => setMeterForm({ ...meterForm, meter_type: e.target.value })}
              required
            >
              <MenuItem value="Power">Power</MenuItem>
              <MenuItem value="Water">Water</MenuItem>
              <MenuItem value="Steam">Steam</MenuItem>
              <MenuItem value="Gas">Gas</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Unit"
              value={meterForm.unit}
              onChange={(e) => setMeterForm({ ...meterForm, unit: e.target.value })}
              placeholder="kWh, m³, kg, etc."
              required
            />
            <TextField
              fullWidth
              select
              label="Assignment Level"
              value={meterForm.assignment_level}
              onChange={(e) => {
                setMeterForm({ ...meterForm, assignment_level: e.target.value, assignment_id: '' });
              }}
              required
            >
              <MenuItem value="Department">Department</MenuItem>
              <MenuItem value="Section">Section</MenuItem>
              <MenuItem value="Machine">Machine</MenuItem>
            </TextField>
            {meterForm.assignment_level === 'Section' && (
              <TextField
                fullWidth
                select
                label="Select Section"
                value={meterForm.assignment_id}
                onChange={(e) => setMeterForm({ ...meterForm, assignment_id: e.target.value })}
                required
              >
                {architecture?.sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.section_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {meterForm.assignment_level === 'Machine' && (
              <TextField
                fullWidth
                select
                label="Select Machine"
                value={meterForm.assignment_id}
                onChange={(e) => setMeterForm({ ...meterForm, assignment_id: e.target.value })}
                required
              >
                {architecture?.sections.flatMap((section) =>
                  section.machines.map((machine) => (
                    <MenuItem key={machine.id} value={machine.id}>
                      {machine.machine_name} ({section.section_name})
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}
            <TextField
              fullWidth
              label="Description"
              value={meterForm.description}
              onChange={(e) => setMeterForm({ ...meterForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeterDialog(false)}>Cancel</Button>
          <Button onClick={handleAddMeter} variant="contained" sx={{ backgroundColor: '#FFC107', color: '#000' }}>
            Add Meter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Meter Dialog */}
      <Dialog open={editMeterDialog} onClose={() => setEditMeterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#FFC107' }}>Edit Meter</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Meter Name"
              value={meterForm.meter_name}
              onChange={(e) => setMeterForm({ ...meterForm, meter_name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Meter Code"
              value={meterForm.meter_code}
              onChange={(e) => setMeterForm({ ...meterForm, meter_code: e.target.value })}
              required
            />
            <TextField
              fullWidth
              select
              label="Meter Type"
              value={meterForm.meter_type}
              onChange={(e) => setMeterForm({ ...meterForm, meter_type: e.target.value })}
              required
            >
              <MenuItem value="Power">Power</MenuItem>
              <MenuItem value="Water">Water</MenuItem>
              <MenuItem value="Steam">Steam</MenuItem>
              <MenuItem value="Gas">Gas</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Unit"
              value={meterForm.unit}
              onChange={(e) => setMeterForm({ ...meterForm, unit: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={meterForm.description}
              onChange={(e) => setMeterForm({ ...meterForm, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMeterDialog(false)}>Cancel</Button>
          <Button onClick={handleEditMeter} variant="contained" sx={{ backgroundColor: '#FFC107', color: '#000' }}>
            Update Meter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Meter Dialog */}
      <Dialog open={moveMeterDialog} onClose={() => setMoveMeterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#FFC107' }}>Move Meter to Different Location</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Moving: <strong>{meterForm.meter_name}</strong>
            </Alert>
            <TextField
              fullWidth
              select
              label="New Assignment Level"
              value={meterForm.assignment_level}
              onChange={(e) => {
                setMeterForm({ ...meterForm, assignment_level: e.target.value, assignment_id: '' });
              }}
              required
            >
              <MenuItem value="Department">Department</MenuItem>
              <MenuItem value="Section">Section</MenuItem>
              <MenuItem value="Machine">Machine</MenuItem>
            </TextField>
            {meterForm.assignment_level === 'Section' && (
              <TextField
                fullWidth
                select
                label="Select Section"
                value={meterForm.assignment_id}
                onChange={(e) => setMeterForm({ ...meterForm, assignment_id: e.target.value })}
                required
              >
                {architecture?.sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.section_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {meterForm.assignment_level === 'Machine' && (
              <TextField
                fullWidth
                select
                label="Select Machine"
                value={meterForm.assignment_id}
                onChange={(e) => setMeterForm({ ...meterForm, assignment_id: e.target.value })}
                required
              >
                {architecture?.sections.flatMap((section) =>
                  section.machines.map((machine) => (
                    <MenuItem key={machine.id} value={machine.id}>
                      {machine.machine_name} ({section.section_name})
                    </MenuItem>
                  ))
                )}
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveMeterDialog(false)}>Cancel</Button>
          <Button onClick={handleMoveMeter} variant="contained" sx={{ backgroundColor: '#FFC107', color: '#000' }}>
            Move Meter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#f44336' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete <strong>{deleteTarget?.type}</strong>: <strong>{deleteTarget?.name}</strong>?
            <br />
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
    </Box>
  );
};

export default MeterArchitecture;
